import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { Compass, LayoutGrid, Hammer, TrendingUp, type LucideIcon } from "lucide-react";

const steps: { key: string; Icon: LucideIcon }[] = [
  { key: "discover",  Icon: Compass },
  { key: "architect", Icon: LayoutGrid },
  { key: "build",     Icon: Hammer },
  { key: "scale",     Icon: TrendingUp },
];

export function ProcessSection({ index = "03" }: { index?: string }) {
  const { t } = useI18n();
  const data = t.process;

  return (
    <section className="border-t hairline bg-foreground/[0.015] relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-grid-fine opacity-30 mask-radial-fade" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
              — {index} / {data.eyebrow}
            </p>
            <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
              {data.title}
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              {data.subtitle}
            </p>
          </Reveal>
        </div>

        {/* horizontal connector for desktop */}
        <div className="relative">
          <div className="hidden lg:block absolute left-0 right-0 top-[44px] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 relative">
            {steps.map(({ key, Icon }, i) => {
              const step = (data.steps as Record<
                string,
                { t: string; d: string; deliverables: string[] }
              >)[key];
              return (
                <Reveal key={key} delay={i * 90}>
                  <div className="group relative h-full rounded-2xl border hairline bg-background p-6 lg:p-7 card-lift">
                    {/* numbered orb */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative inline-flex h-14 w-14 items-center justify-center rounded-full border hairline bg-background transition-colors group-hover:bg-foreground group-hover:border-foreground">
                        <Icon
                          className="h-5 w-5 transition-colors group-hover:text-background"
                          strokeWidth={1.5}
                        />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
                          Phase 0{i + 1}
                        </p>
                        <h3 className="font-display text-xl font-semibold tracking-tight">
                          {step.t}
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">{step.d}</p>

                    <div className="border-t hairline pt-4">
                      <ul className="space-y-2">
                        {step.deliverables.map((d) => (
                          <li
                            key={d}
                            className="flex items-start gap-2 text-xs text-muted-foreground"
                          >
                            <span className="mt-[6px] h-1 w-1 rounded-full bg-foreground/50 flex-shrink-0" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}